import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '@app/core/services/loader/loader.service';
import { NotificationService } from '@app/core/services/notification/notification.service';
import { StateService } from '@app/shared/services/state/state.service';
import { Location } from '@angular/common';
import { getRoutePrefix } from '@app/shared/utils/route-prefix.util';
import { UploadDesignService } from '@app/feature/store-admin/services/uploadDesign/uploadDesign.service';
import { ErrorhandlerService } from '@app/shared/services/errorhandler/errorhandler.service';
import { PdfThumbnailService } from '@app/shared/services/pdfThumbnail/pdf-thumbnail.service';
import { CommonService } from '@app/shared/services/common/common.service';
import { environment } from '@env/environment';

// Rasterization width for the full-size PDF preview, in CSS pixels.
const PDF_PREVIEW_WIDTH = 1000;

interface SavedDesign {
  id: number;
  name: string;
  thumbnailUrl: string;
  createdDate: Date;
  designId: number;
}

@Component({
  selector: 'app-design-preview',
  templateUrl: './design-preview.component.html',
  styleUrls: ['./design-preview.component.css']
})
export class DesignPreviewComponent implements OnInit {
  storeId: number;
  storeName: string;
  superAdminLoggedIn: boolean = false;
  userRole: string;

  // Design data
  selectedDesign: SavedDesign | null = null;
  selectedFile: File | null = null;
  previewUrl: string = '';
  designId: number | null = null;
  logoUrl: string = '';  // Azure blob URL for uploaded logo
  fileName: string = '';
  isEditMode: boolean = false;
  editProductData: any = null;
  // True only for a freshly uploaded design (not a saved one, not edit mode) —
  // this is the case that still needs a row created in UploadedDesigns.
  isNewUpload: boolean = false;
  // Page 1 of a PDF design, rasterized to a PNG data URL so it can be shown in a plain <img>
  // (a browser cannot decode a PDF inside one). Null until the render lands, or if it failed.
  pdfImageUrl: string | null = null;
  // True while that render is in flight.
  isRenderingPdf: boolean = false;
  // Fallback for a PDF that could not be rasterized: the browser's own viewer via <object>,
  // which needs no CORS. Held as a field rather than a getter — bypassSecurityTrustResourceUrl
  // returns a new object each call, and re-binding [data] every change-detection pass would
  // make the embedded document reload continuously.
  pdfPreviewUrl: SafeResourceUrl | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private loader: LoaderService,
    private notification: NotificationService,
    private stateService: StateService,
    private location: Location,
    private uploadDesignService: UploadDesignService,
    private errorHandler: ErrorhandlerService,
    private sanitizer: DomSanitizer,
    private pdfThumbnail: PdfThumbnailService,
    private commonService: CommonService
  ) {
    this.route.queryParams.subscribe(params => {
      this.storeId = params['storeId'] || localStorage.getItem('storeId');
      this.storeName = params['storeName'] || localStorage.getItem('storeName') || '';
      this.designId = params['designId'];
    });
  }

  ngOnInit(): void {
    this.userRole = localStorage.getItem('role');
    if (this.userRole === 'SuperAdmin' || this.userRole === 'CustomerService') {
      this.superAdminLoggedIn = true;
    }

    // Check if we're in edit mode
    this.isEditMode = this.stateService.isEditMode();
    this.editProductData = this.stateService.getEditProductData();

    // Get design data from state or query params
    this.loadDesignData();
  }

  loadDesignData(): void {
    // Check if we have design data in state service
    const designData = this.stateService.getDesignData();

    if (designData) {
      if (designData.type === 'saved') {
        this.selectedDesign = designData.design;
        this.previewUrl = this.selectedDesign.thumbnailUrl;
        this.logoUrl = this.selectedDesign.thumbnailUrl;
      } else if (designData.type === 'uploaded') {
        this.selectedFile = designData.file;
        // Use Azure blob URL if available, otherwise generate from file
        if (designData.logoUrl) {
          this.logoUrl = designData.logoUrl;
          this.fileName = designData.fileName || '';
          this.previewUrl = designData.logoUrl;
          // Fresh upload from the upload page → will be persisted to UploadedDesigns
          // on "Continue to Product". Edit flows reuse an existing design, so skip them.
          this.isNewUpload = !this.isEditMode;
        } else if (designData.previewUrl) {
          // Edit mode: use previewUrl directly (already a full URL)
          this.previewUrl = designData.previewUrl;
          this.logoUrl = designData.designUrl || designData.previewUrl;
        } else if (this.selectedFile) {
          this.generatePreviewFromFile(this.selectedFile);
        }
      }
    } else if (this.designId) {
      // Load from designId if available
      this.loadDesignById(this.designId);
    } else {
      // No design data available, redirect back
      this.notification.error('No design selected. Please select a design first.', 'Error');
      this.goBack();
    }

    this.refreshPdfPreview();
  }

  // Recomputes the PDF preview state from the current previewUrl. Call after every assignment
  // to previewUrl.
  private refreshPdfPreview(): void {
    this.pdfImageUrl = null;
    this.pdfPreviewUrl = null;
    this.isRenderingPdf = false;

    if (!this.isEmbeddablePdf(this.previewUrl)) {
      return;
    }

    // Preferred path: rasterize page 1 and show it as an ordinary image. Rendered wider than the
    // grid thumbnails since this is the full-size preview.
    const source = this.previewUrl;
    this.isRenderingPdf = true;
    this.pdfThumbnail.render(source, PDF_PREVIEW_WIDTH).then((dataUrl) => {
      // The user may have navigated to a different design while this was rendering.
      if (source !== this.previewUrl) { return; }

      this.isRenderingPdf = false;
      this.pdfImageUrl = dataUrl;

      if (!dataUrl) {
        // Rasterization failed (corrupt file, blocked fetch). Fall back to the browser's own PDF
        // viewer, which loads the blob directly and needs no CORS.
        // Hiding the viewer chrome keeps this reading as a preview rather than a reader.
        this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          source + '#toolbar=0&navpanes=0&view=FitH'
        );
      }
    });
  }

  // Only http(s) PDFs are embedded. UploadHuddleLogoHandler stores PDFs with an inline
  // Content-Disposition (the "attachment" header is applied to SVG only), so the browser renders
  // page 1 directly from the blob URL — no CORS on the storage account required. A data: URI is
  // deliberately excluded: browsers block plugin documents loaded from data: URLs, so that case
  // falls through to the file tile instead of showing an empty frame.
  private isEmbeddablePdf(url: string): boolean {
    if (!url || !/^https?:/i.test(url)) { return false; }
    return url.split(/[?#]/)[0].toLowerCase().endsWith('.pdf');
  }

  // True when the preview is a PDF, whether or not it can be embedded — drives the file tile.
  get isPdfDesign(): boolean {
    if (!this.previewUrl) { return false; }
    return this.previewUrl.toLowerCase().startsWith('data:application/pdf')
      || this.previewUrl.split(/[?#]/)[0].toLowerCase().endsWith('.pdf');
  }

  loadDesignById(designId: number): void {
    // TODO: Replace with actual API call to get design by ID
    // this.designService.getDesignById(designId).subscribe(
    this.loader.show();
    setTimeout(() => {
      this.selectedDesign = {
        id: designId,
        name: 'Design #' + designId,
        thumbnailUrl: 'assets/img/design-thumb-1.png',
        createdDate: new Date(),
        designId: designId
      };
      this.previewUrl = this.selectedDesign.thumbnailUrl;
      this.refreshPdfPreview();
      this.loader.hide();
    }, 500);
    // );
  }

  generatePreviewFromFile(file: File): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.previewUrl = e.target.result;
      this.refreshPdfPreview();
    };
    reader.readAsDataURL(file);
  }

  getFileExtension(): string {
    if (this.selectedFile) {
      return this.selectedFile.name.split('.').pop()?.toUpperCase() || 'Unknown';
    } else if (this.selectedDesign) {
      return this.selectedDesign.thumbnailUrl.split('.').pop()?.toUpperCase() || 'Unknown';
    }
    return 'Unknown';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  getCurrentDateTime(): string {
    return new Date().toLocaleString();
  }

  goBack(): void {
    const prefix = getRoutePrefix(this.router.url);
    if (this.isEditMode) {
      // In edit mode, go back to shopify products page
      this.stateService.clearEditMode();
      if (prefix === '/store') {
        this.router.navigate(['/store/publishedproducts']);
      } else {
        this.router.navigate(['/superadmin/shopify-products', this.storeId, this.storeName]);
      }
    } else {
      this.router.navigate([`${prefix}/upload-design`], {
        queryParams: { storeId: this.storeId, storeName: this.storeName }
      });
    }
  }

  changeDesign(): void {
    const prefix = getRoutePrefix(this.router.url);
    this.router.navigate([`${prefix}/upload-design`], {
      queryParams: { storeId: this.storeId, storeName: this.storeName }
    });
  }

  continueToProductSelection(): void {
    // Designs saved before PDFs were converted at upload time still point at a .pdf. Design Huddle
    // takes this URL verbatim as the project's logo media (classes.logo.url) and cannot read a
    // PDF, so the product mockups come back with no artwork on them. Convert to PNG first.
    if (this.pdfThumbnail.isPdfUrl(this.logoUrl || this.previewUrl)) {
      this.convertPdfDesignToImage().then((converted) => {
        if (!converted) {
          this.notification.error(
            'This PDF design could not be converted to an image. Please re-upload it.',
            'Error'
          );
          return;
        }
        this.saveAndProceed();
      });
      return;
    }

    this.saveAndProceed();
  }

  /**
   * Re-uploads a legacy PDF design as a PNG and repoints this design at it.
   *
   * The upload endpoint names blobs by content hash, so converting the same design twice reuses
   * the existing blob rather than filling storage with duplicates.
   */
  private convertPdfDesignToImage(): Promise<boolean> {
    const source = this.logoUrl || this.previewUrl;

    this.loader.setPersistentMessage('Converting PDF design to image');
    this.loader.show();

    return this.pdfThumbnail.rasterizePdfUrl(source)
      .then((dataUrl) => {
        if (!dataUrl) { return false; }

        return this.commonService
          .uploadHuddleLogo({ File: dataUrl.split(',')[1], Extension: 'png' })
          .toPromise()
          .then((response: any) => {
            if (!response || response.StatusCode !== 200) { return false; }

            const fileName = response.Data.ResponseData;
            this.logoUrl = environment.huddleLogoFolder + fileName;
            this.previewUrl = this.logoUrl;
            this.fileName = fileName;

            // The design now resolves as an image everywhere downstream, so drop the PDF state.
            this.refreshPdfPreview();

            // Deliberately does NOT write a new UploadedDesigns row. The saved design already has
            // one pointing at the PDF, and adding a second would list the same artwork twice with
            // no way to tell them apart. Re-converting on each use is cheap — the upload dedupes
            // on content hash, so it returns the existing blob rather than storing another copy.
            return true;
          });
      })
      .catch(() => false)
      .then((ok) => {
        this.loader.clearPersistentMessage();
        this.loader.hide();
        return ok;
      });
  }

  private saveAndProceed(): void {
    // For a freshly uploaded design, save a row in UploadedDesigns first, then continue.
    // Saved designs (already in the table) and edit flows skip this and go straight through.
    if (this.isNewUpload && this.logoUrl) {
      this.loader.show();
      this.uploadDesignService.uploadDesign(this.buildUploadedDesignPayload()).subscribe(
        (res: any) => {
          if (res && res.StatusCode === 200) {
            const alreadySaved = res.Message === 'Design already exists.';
            this.notification.success(
              alreadySaved ? 'Design already saved, using existing entry.' : 'Design saved.',
              'Success'
            );
          }
          this.proceedToProductSelection();
        },
        (err) => {
          // Don't block the product flow if the save fails — surface the error and continue.
          this.errorHandler.handleError(err);
          this.proceedToProductSelection();
        }
      );
    } else {
      this.proceedToProductSelection();
    }
  }

  // Builds the CreateUploadDesignCommand payload for the current uploaded design.
  private buildUploadedDesignPayload(): any {
    return {
      UploadedFileName: this.fileName,
      AzureBlobDesignFileUrl: this.logoUrl,
      UploadedBase64FileUrl: '',
      // Prefer the name the user recognises. this.fileName is a content hash, so it is only a
      // last resort — a converted PDF design would otherwise be listed as "<hash>.png".
      DesignName: this.selectedFile?.name || this.selectedDesign?.name || this.fileName || 'Untitled Design',
      DesignDescription: '',
      DesignPrice: 0,
      CreatedBy: Number(localStorage.getItem('userId')) || 0,
      // Scopes the design to this store's owner so it shows in the Saved Designs tab.
      StoreId: Number(this.storeId) || 0
    };
  }

  private proceedToProductSelection(): void {
    // Store design data for the next step
    const designData = {
      design: this.selectedDesign,
      file: this.selectedFile,
      name: this.selectedDesign?.name || this.selectedFile?.name || 'Design',
      designId: this.designId || this.selectedDesign?.designId,
      previewUrl: this.previewUrl,
      designUrl: this.logoUrl || this.previewUrl, // Use Azure blob URL instead of base64
      logoUrl: this.logoUrl,
      fileName: this.fileName,
      type: this.selectedDesign ? 'saved' : 'uploaded',
      isEdit: this.isEditMode
    };


    // Set in both state locations for compatibility
    this.stateService.setDesignDataForProduct(designData);
    this.stateService.setDesignData(designData);

    // Clear old selections from previous flow when starting a new add flow
    if (!this.isEditMode) {
      this.stateService.clearSelectedProductsForDesign();
      this.stateService.clearSelectedProductsForColors();
      this.stateService.clearColorVariantsData();
      this.stateService.clearStagedColorwayState();
    }

    // Set a persistent loader message that survives the intermediate hide() calls
    // inside MasterProductListingComponent (fn_GetFilters, fn_GetProducts) so it
    // stays visible until the final preview-generation terminal point clears it.
    this.loader.setPersistentMessage('Generating product preview');
    this.loader.show();

    // Navigate to product selection (master products listing)
    const prefix = getRoutePrefix(this.router.url);
    this.router.navigate([`${prefix}/masterproducts`], {
      queryParams: {
        storeId: this.storeId,
        storeName: this.storeName,
        designId: designData.designId,
        editMode: this.isEditMode || undefined
      }
    });
  }
}
